import { Alert, Button, Checkbox, Divider, Form, Input, type InputRef } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DecryptedFileSecretData, SecretFilePresetType } from '../../../interfaces/FileSecret';
import ModalLayout from '../../../layouts/ModalLayout';
import { createSecretFilePreset, resolveSecretFilePreset } from '../../../utils/fileSecretPresets';
import { FileViewerSecretEditor } from '../../Files/FileViewerSecretEditor';

const SECRET_FILE_FORM_ID = 'secret-file-form';

interface Props {
  preset?: SecretFilePresetType;
  onSubmit: (data: DecryptedFileSecretData, password?: string) => void;
  onCancel: () => void;
}

/**
 * SecretFileForm — create a .fgsecret from a preset template.
 * ModalLayout: scrollable body, pinned Cancel/Save footer (tall presets e.g. Identity).
 */
export const SecretFileForm = ({
  preset: presetProp = 'credentials',
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);
  const [usePassword, setUsePassword] = useState(false);

  const resolvedPreset = resolveSecretFilePreset(presetProp);

  const [secretData, setSecretData] = useState<DecryptedFileSecretData>(() =>
    createSecretFilePreset(resolvedPreset, t),
  );

  // Remount path: when parent switches preset, rebuild template.
  useEffect(() => {
    setSecretData(createSecretFilePreset(resolvedPreset, t));
  }, [resolvedPreset, t]);

  const handleFinish = (values: {
    fileName: string;
    password?: string;
    confirmPassword?: string;
    hint?: string;
  }) => {
    const finalData: DecryptedFileSecretData = {
      ...secretData,
      name: values.fileName,
      encrypted: usePassword,
      hint: usePassword ? values.hint?.trim() || undefined : undefined,
      schemaVersion: 2,
    };
    onSubmit(finalData, usePassword ? values.password : undefined);
  };

  const handleSecretDataChange = (data: DecryptedFileSecretData) => {
    setSecretData(data);
  };

  useEffect(() => {
    form.setFieldsValue({
      fileName: t('secret.newSecret'),
    });

    // Custom: autofocus first field value in editor instead of file name.
    if (resolvedPreset === 'custom') return;

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 200);
  }, [form, t, resolvedPreset]);

  return (
    <ModalLayout>
      <ModalLayout.Content>
        <Form
          id={SECRET_FILE_FORM_ID}
          layout="vertical"
          form={form}
          size="large"
          onFinish={handleFinish}
        >
          <Form.Item
            name="fileName"
            label={t('secret.fileName')}
            className="mb-2!"
            normalize={(v: string) => v.replace(/\s{3,}/g, ' ').trimStart()}
            rules={[
              {
                required: true,
                message: t('secret.fileNameRequired'),
                transform: (value: string) => value?.trim(),
              },
            ]}
          >
            <Input
              ref={inputRef}
              autoFocus={resolvedPreset !== 'custom'}
              autoCapitalize="none"
              placeholder={t('secret.fileName')}
              maxLength={255}
            />
          </Form.Item>

          <div
            className={
              usePassword
                ? 'border-gray-medium flex flex-col rounded-lg border p-3'
                : 'border-gray-medium rounded-lg border px-3 py-2'
            }
            data-testid="secret-protect-section"
          >
            <Checkbox
              className={usePassword ? 'mb-2' : undefined}
              checked={usePassword}
              onChange={(e) => setUsePassword(e.target.checked)}
            >
              {t('secret.protectWithSecretKey')}
            </Checkbox>
            {usePassword && (
              <>
                <Form.Item
                  name="password"
                  label={t('secret.enterPassword')}
                  className="mb-1!"
                  rules={[
                    {
                      required: usePassword,
                      message: t('secret.passwordRequired'),
                    },
                    {
                      min: 4,
                      message: t('secret.passwordMinLength'),
                    },
                  ]}
                >
                  <Input.Password placeholder={t('secret.enterPassword')} />
                </Form.Item>

                <Form.Item
                  className="mb-1!"
                  name="confirmPassword"
                  label={t('secret.confirmPassword')}
                  dependencies={['password']}
                  rules={[
                    {
                      required: usePassword,
                      message: t('secret.confirmPasswordRequired'),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('secret.passwordMismatch')));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder={t('secret.confirmPassword')} />
                </Form.Item>

                <Form.Item name="hint" label={t('secret.hint')} className="mb-0!">
                  <Input placeholder={t('secret.hintPlaceholder')} maxLength={100} />
                </Form.Item>
                <Alert
                  type="warning"
                  showIcon
                  className="mt-3!"
                  title={t('secret.secretKeyWarning')}
                />
              </>
            )}
          </div>
        </Form>

        {/* Outside Form: Form size="large" must not cascade into editor Buttons/Inputs. */}
        <Divider className="border-gray-medium my-3!" />

        <FileViewerSecretEditor
          key={resolvedPreset}
          mode="edit"
          initialData={secretData}
          onChange={handleSecretDataChange}
          showHeader={false}
          showFieldSearch={false}
          autoFocusFirstField={resolvedPreset === 'custom'}
        />
      </ModalLayout.Content>

      <ModalLayout.Footer>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button size="middle" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button size="middle" type="primary" form={SECRET_FILE_FORM_ID} htmlType="submit">
            {t('common.save')}
          </Button>
        </div>
      </ModalLayout.Footer>
    </ModalLayout>
  );
};

export default SecretFileForm;
